import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

/**
 * Operator-facing error copy keyed by a mutation's `mutationKey`. The trading
 * on/off controls get an explicit "may still be ACTIVE" warning: a silently
 * dropped off-toggle is how a rule the operator believed was off kept trading
 * (Roberto Sánchez opener, 2026-06-09 — the optimistic switch painted OFF but
 * the request never reached the backend and nothing surfaced the failure).
 * Untagged mutations fall back to the generic message.
 */
export const MUTATION_ERROR_MESSAGES: Record<string, string> = {
  'entry:create-rule':
    'Failed to create the opener rule — it was NOT created.',
  'entry:set-maker-flag':
    'Failed to change the opener rule — it may still be ACTIVE. Retry and confirm the switch.',
  'entry:toggle-platform':
    'Failed to toggle the opener platform — the rule may still be ACTIVE. Retry and confirm the switch.',
  'unwinding:pause':
    'Failed to pause the unwinder rule — it may still be ACTIVE. Retry and confirm the switch.',
  'unwinding:toggle-platform':
    'Failed to toggle the unwinder platform — the rule may still be ACTIVE. Retry and confirm the switch.',
  'entry:group-start':
    'Failed to start the group pods — the group is NOT running.',
  'entry:group-stop':
    'Failed to stop the group — its rules may still be ACTIVE and its pods may still be running.',
  'entry:group-panic-soft':
    'Group SOFT PANIC failed — orders may still be resting and rules may still be ACTIVE.',
  'entry:group-panic':
    'Group HARD PANIC failed — the engine pod may still be running and orders may still be resting.'
};

export const DEFAULT_MUTATION_ERROR_MESSAGE =
  'Action failed — server state may be unchanged. Please retry.';

/**
 * Message-stem routing for the groups-only trading rejection. Activation
 * mutations (`entry:set-maker-flag`, `entry:toggle-platform`) are tagged
 * with the "may still be ACTIVE" copy — but when the backend refused the
 * activation BECAUSE the pair has no opener group, that copy is wrong (the
 * rule is definitively NOT active) and unactionable. Matching on the stable
 * stem of the backend message (not the full phrasing, which may drift)
 * routes this case to copy that tells the operator exactly what to do.
 */
export const GROUPS_ONLY_REJECTION_PATTERN =
  /not assigned to an opener group/i;

export const GROUPS_ONLY_REJECTION_MESSAGE =
  'Rule NOT activated — the pair is not assigned to an opener group; assign it in the Pairs Bucket and retry.';

/**
 * Best-effort extraction of the backend's rejection reason from an axios
 * error (NestJS error body: `message` as a string, or string[] from the
 * ValidationPipe). The generic toast used to swallow these — a precise 400
 * like "thresholdSum must be < 1.0" surfaced as an opaque "Action failed",
 * leaving the operator guessing which input the server refused.
 */
export function extractServerErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } } | null | undefined)
    ?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const message = (data as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) {
    const parts = message.filter(
      (m): m is string => typeof m === 'string' && m.trim().length > 0
    );
    if (parts.length) return parts.join('; ');
  }
  return null;
}

export function resolveMutationErrorMessage(
  mutationKey: unknown,
  error?: unknown
): string {
  const serverMessage = extractServerErrorMessage(error);
  // Groups-only activation rejection overrides BOTH the keyed copy and the
  // generic fallback: the keyed "may still be ACTIVE" warning would mislead
  // here — the backend refused the activation, so the rule is NOT active.
  if (serverMessage && GROUPS_ONLY_REJECTION_PATTERN.test(serverMessage)) {
    return GROUPS_ONLY_REJECTION_MESSAGE;
  }
  if (
    typeof mutationKey === 'string' &&
    Object.prototype.hasOwnProperty.call(MUTATION_ERROR_MESSAGES, mutationKey)
  ) {
    const tailored = MUTATION_ERROR_MESSAGES[mutationKey];
    return serverMessage ? `${tailored} ${serverMessage}` : tailored;
  }
  return serverMessage
    ? `Action failed — ${serverMessage}`
    : DEFAULT_MUTATION_ERROR_MESSAGE;
}

/**
 * Single source of truth for surfacing a failed react-query mutation to the
 * operator. Wired app-wide via the QueryClient's MutationCache so NO mutation
 * failure is ever silent — the optimistic-toggle rollback reverts the switch,
 * this makes the failure visible and reports it to Sentry.
 */
export function handleMutationError(error: unknown, mutationKey?: unknown): void {
  toast(resolveMutationErrorMessage(mutationKey, error), {
    theme: 'dark',
    type: 'error',
    autoClose: 8000,
    pauseOnFocusLoss: false
  });
  Sentry.captureException(error, {
    extra: {
      mutationKey: typeof mutationKey === 'string' ? mutationKey : null,
      serverMessage: extractServerErrorMessage(error)
    }
  });
}
